import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Routes } from '../../../routes.types';
import {
    groupedLineClicked,
    groupedTicketClicked,
    lineClicked,
    popOverClicked,
    PopOverType,
    ticketClicked,
    undisplayGroupedTickets,
    undisplayTicket,
} from '../../../redux/tickets/user.actions';
import { getCompactModeState, getGreyUnpaidOrdersState } from '../../../redux/localConfig/selector';
import { TicketStatus } from '../../../redux/tickets/types/ticket';

type PropsVm = {
    isDisplayMode: boolean;
    isFullOrderNotPaid: boolean;
    route: Routes;
    ticketStatus?: TicketStatus;
};

type ReturnTypeVm = {
    isCompactMode: boolean;
    dispatchUndisplayTicketClicked: (ordersId: string) => void;
    dispatchGroupedUndisplayTickets: (ordersIds: string[]) => void;
    dispatchGroupedTicketClicked: (orderIds: string[]) => void;
    dispatchGroupedLineClicked: (clickedPath: string, lines: { orderId: string; linePath: string }[]) => void;
    dispatchTicketClicked: (orderId: string) => void;
    dispatchLineClicked: (orderId: string, linePath: string) => void;
    dispatchStatusChange: (orderIds: string[], type: PopOverType, dispatchRoute: Routes) => void;
};

export const useTicketCardVm = ({ isDisplayMode, isFullOrderNotPaid, route, ticketStatus }: PropsVm): ReturnTypeVm => {
    const dispatch = useDispatch();
    const isCompactMode = useSelector(getCompactModeState);
    const greyUnpaidOrdersOption = useSelector(getGreyUnpaidOrdersState);
    const isTicketToCancelOrModified =
        !!ticketStatus && [TicketStatus.ToCancel, TicketStatus.Modified].includes(ticketStatus);
    const canDispatchTicket =
        !isDisplayMode && (!greyUnpaidOrdersOption || !isFullOrderNotPaid || isTicketToCancelOrModified);

    const dispatchUndisplayTicketClicked = useCallback(
        (ordersId: string) => {
            if (canDispatchTicket) {
                dispatch(undisplayTicket(ordersId));
            }
        },
        [dispatch, canDispatchTicket],
    );

    const dispatchGroupedUndisplayTickets = useCallback(
        (ordersIds: string[]) => {
            if (canDispatchTicket) {
                dispatch(undisplayGroupedTickets(ordersIds));
            }
        },
        [dispatch, canDispatchTicket],
    );

    const dispatchGroupedTicketClicked = useCallback(
        (orderIds: string[]) => {
            if (canDispatchTicket) {
                dispatch(groupedTicketClicked(orderIds, route));
            }
        },
        [dispatch, canDispatchTicket, route],
    );

    const dispatchGroupedLineClicked = useCallback(
        (clickedLinePath: string, lines: { orderId: string; linePath: string }[]) => {
            if (canDispatchTicket) {
                dispatch(groupedLineClicked(clickedLinePath, lines, route));
            }
        },
        [dispatch, canDispatchTicket, route],
    );

    const dispatchTicketClicked = useCallback(
        (orderId: string) => {
            if (canDispatchTicket) {
                dispatch(ticketClicked(orderId, route));
            }
        },
        [dispatch, canDispatchTicket, route],
    );

    const dispatchLineClicked = useCallback(
        (orderId: string, linePath: string) => {
            if (canDispatchTicket) {
                dispatch(lineClicked(orderId, linePath, route));
            }
        },
        [dispatch, canDispatchTicket, route],
    );

    const dispatchStatusChange = useCallback(
        (orderIds: string[], type: PopOverType, dispatchRoute: Routes) => {
            if (canDispatchTicket) {
                dispatch(popOverClicked(orderIds, type, dispatchRoute));
            }
        },
        [dispatch, canDispatchTicket],
    );

    return {
        isCompactMode,
        dispatchUndisplayTicketClicked,
        dispatchGroupedUndisplayTickets,
        dispatchGroupedTicketClicked,
        dispatchGroupedLineClicked,
        dispatchTicketClicked,
        dispatchLineClicked,
        dispatchStatusChange,
    };
};
